import React, { useState, useMemo, useEffect } from "react";
import { dataMap } from "@/constants/distribution";
import { rebuildFormula } from "@/config/func";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { formatBySig, resolveSig } from "@/utils/numberFormat";
import "./distribution.less";

// 判断当前值与对比值是否有差异（与其它结果表口径一致：相对容差 1e-9，任一侧缺失不算差异）
const valuesDiffer = (current: any, compare: any): boolean => {
  const curMissing = current === "-" || current === null || current === undefined;
  const cmpMissing = compare === "-" || compare === null || compare === undefined;
  if (curMissing || cmpMissing) return false;
  const curStr = String(current);
  const cmpStr = String(compare);
  const curNum = parseFloat(curStr);
  const cmpNum = parseFloat(cmpStr);
  if (!isNaN(curNum) && !isNaN(cmpNum)) {
    if (curNum === cmpNum) return false;
    const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
    return Math.abs(curNum - cmpNum) > tolerance;
  }
  return curStr !== cmpStr;
};

const DistributionResultDataTable = (props: any) => {
  const {
    currentMode,        // 当前结果标记
    currentModeData,    // 当前结果数据
    currentMaterial,    // 当前选择组分
    unit,               // 自定义单位（覆盖dataMap默认单位）
    compareTableData = [], // 对比数据: [{taskId, taskName, data: []}]
  } = props;

  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16; // 每页显示20条数据（10行，每行2个塔板）
  
  // 获取表格标题
  const getTitle = () => {
    if (["K", "X", "Y"].includes(currentMode) && currentMaterial) {
      return `${rebuildFormula(currentMaterial?.Molecular_formula)}${dataMap[currentMode]?.title || ""}`;
    } else {
      return `${dataMap[currentMode]?.title || currentMode}`;
    }
  };

  // 切换数据时重置页码到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [currentModeData]);

  // 将数据处理成表格行
  const processDataForTable = () => {
    if (!currentModeData || !Array.isArray(currentModeData)) {
      return [];
    }

    const hasCompare = compareTableData.length > 0;

    if (hasCompare) {
      // 有对比：每个塔板一行，横排显示所有对比值
      return currentModeData.map((value, i) => ({
        index: i + 1,
        mainValue: value ?? "-",
        compareValues: compareTableData.map((cmp: any) => ({
          taskName: cmp.taskName,
          value: cmp.data?.[i] != null ? cmp.data[i] : "-",
        })),
      }));
    } else {
      // 无对比：保持原两列布局
      const processedData = [];
      for (let i = 0; i < currentModeData.length; i += 2) {
        processedData.push({
          firstPlate: {
            index: i + 1,
            value: currentModeData[i] ?? "-",
          },
          secondPlate: i + 1 < currentModeData.length ? {
            index: i + 2,
            value: currentModeData[i + 1] ?? "-",
          } : null,
        });
      }
      return processedData;
    }
  };

  // 使用useMemo优化性能，避免重复计算
  const allProcessedData = useMemo(() => processDataForTable(), [currentModeData, compareTableData]);
  
  const hasCompare = compareTableData.length > 0;

  // 计算总页数
  const totalPages = Math.max(1, Math.ceil(allProcessedData.length / (hasCompare ? itemsPerPage : itemsPerPage / 2)));
  
  // 获取当前页的数据
  const currentPageData = useMemo(() => {
    const pageSize = hasCompare ? itemsPerPage : itemsPerPage / 2;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allProcessedData.slice(startIndex, endIndex);
  }, [allProcessedData, currentPage, itemsPerPage]);

  // 处理页码变更
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 生成页码按钮
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;
    
    // 总是显示第一页
    buttons.push(
      <button
        key={1}
        className={`pagination-button ${currentPage === 1 ? "pagination-current-page" : ""}`}
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      >
        <span className={`pagination-button-text ${currentPage === 1 ? "pagination-current-page-text" : ""}`}>1</span>
      </button>
    );
    
    // 如果页数多于5页，显示省略号
    if (totalPages > maxVisibleButtons) {
      // 计算中间显示的页码范围
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // 确保显示足够的页码
      if (endPage - startPage < 2) {
        if (currentPage <= totalPages - 3) {
          endPage = startPage + 2;
        } else if (currentPage >= 4) {
          startPage = endPage - 2;
        }
      }
      
      // 第一页和中间页之间的省略号
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="pagination-info">...</span>
        );
      }
      
      // 中间的页码按钮
      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            className={`pagination-button ${currentPage === i ? "pagination-current-page" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            <span className={`pagination-button-text ${currentPage === i ? "pagination-current-page-text" : ""}`}>{i}</span>
          </button>
        );
      }
      
      // 最后一页和中间页之间的省略号
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="pagination-info">...</span>
        );
      }
    } else {
      // 页数不多于5页时，显示所有页码
      for (let i = 2; i <= totalPages - 1; i++) {
        buttons.push(
          <button
            key={i}
            className={`pagination-button ${currentPage === i ? "pagination-current-page" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            <span className={`pagination-button-text ${currentPage === i ? "pagination-current-page-text" : ""}`}>{i}</span>
          </button>
        );
      }
    }
    
    // 总是显示最后一页（如果不是第一页）
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          className={`pagination-button ${currentPage === totalPages ? "pagination-current-page" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          <span className={`pagination-button-text ${currentPage === totalPages ? "pagination-current-page-text" : ""}`}>{totalPages}</span>
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="result-data-table-container">
      {/* 表格标题 */}
      <h3 className="table-title-text">
        {getTitle()}数据：
        {dataMap[currentMode]?.unit && (
          <span className="table-unit-text">单位：{unit || dataMap[currentMode].unit}</span>
        )}
      </h3>
      
      {/* 表格容器 */}
      {hasCompare ? (
        // 对比模式：表头与表体共享横向滚动
        <div className="table-wrapper table-wrapper-compare">
          <div className="table-scroll-h">
            <div className="table-header table-header-compare">
              <div className="header-cell plate-index-header">塔板编号</div>
              <div className="header-cell plate-value-header" style={{ color: "#0148e0" }}>当前任务</div>
              {compareTableData.map((cmp: any, ci: number) => (
                <div key={ci} className="header-cell plate-value-header" style={{ color: "#ffa318" }}>{cmp.taskName}</div>
              ))}
            </div>
            <div className="table-data-view">
              {currentPageData.length > 0 ? (
                currentPageData.map((row: any, rowIndex: number) => {
                  // 该塔板行（当前任务 + 各对比任务）内自适应精度：常态精简小数位，出现差异时逐级提升，保证差异在显示上也能看出来
                  const rowSig = resolveSig(
                    [row.mainValue, ...row.compareValues.map((cv: any) => cv.value)],
                    valuesDiffer
                  );
                  return (
                    <div key={rowIndex} className="table-row table-row-compare">
                      <div className="table-cell plate-index-cell">塔板 {row.index}</div>
                      <div className="table-cell plate-value-cell" style={{ color: "#0148e0", fontWeight: 500 }} title={String(row.mainValue)}>{formatBySig(row.mainValue, rowSig)}</div>
                      {row.compareValues.map((cv: any, ci: number) => (
                        <div key={ci} className="table-cell plate-value-cell" style={{ color: cv.value === "-" ? "#999" : "#ffa318" }} title={String(cv.value)}>{formatBySig(cv.value, rowSig)}</div>
                      ))}
                    </div>
                  );
                })
              ) : (
                <div className="table-row empty-row">
                  <div className="table-cell empty-text">暂无数据</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // 无对比：原布局
        <div className="table-wrapper">
          <div className="table-header">
            <div className="header-cell plate-index-header">塔板编号</div>
            <div className="header-cell plate-value-header">{getTitle()}</div>
            <div className="header-cell plate-index-header">塔板编号</div>
            <div className="header-cell plate-value-header">{getTitle()}</div>
          </div>
          <div className="table-data-view">
            {currentPageData.length > 0 ? (
              currentPageData.map((row: any, rowIndex: number) => (
                <div key={rowIndex} className="table-row">
                  <div className="table-cell plate-index-cell">塔板 {row.firstPlate.index}</div>
                  <div className="table-cell plate-value-cell" title={String(row.firstPlate.value)}>{formatBySig(row.firstPlate.value)}</div>
                  {row.secondPlate ? (
                    <>
                      <div className="table-cell plate-index-cell">塔板 {row.secondPlate.index}</div>
                      <div className="table-cell plate-value-cell" title={String(row.secondPlate.value)}>{formatBySig(row.secondPlate.value)}</div>
                    </>
                  ) : (
                    <>
                      <div className="table-cell empty-cell"></div>
                      <div className="table-cell empty-cell"></div>
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="table-row empty-row">
                <div className="table-cell empty-text">暂无数据</div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 分页控件 */}
      {totalPages > 1 && (
        <div className="pagination-container">
          {/* 上一页按钮 - 只在非第一页显示 */}
          {currentPage > 1 && (
            <button
              className="pagination-button prev-btn"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <LeftOutlined style={{ fontSize: 18, color: '#333' }} />
            </button>
          )}
          
          {/* 页码按钮 */}
          {renderPaginationButtons()}
          
          {/* 下一页按钮 - 只在非最后一页显示 */}
          {currentPage < totalPages && (
            <button
              className="pagination-button next-btn"
              onClick={() => handlePageChange(currentPage + 1)}
            >
               <RightOutlined style={{ fontSize: 18, color: '#333' }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DistributionResultDataTable;